/**
 * ExamTour.tsx
 * React Joyride guided tour overlay
 */

import { useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useTourStore } from '@vsat/shared/hooks/useTourStore';

interface Props {
  onComplete: () => void;
}

export default function ExamTour({ onComplete }: Props) {
  const { t, i18n } = useTranslation();
  const { isRunning, endTour } = useTourStore();

  const steps: Step[] = [
    {
      target: '[data-tour="top-timer"]',
      content: t('tour.steps.timer', 'Exam timer and session controls'),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-overview"]',
      content: t('tour.steps.overview', 'Overview: progress, answered/flagged status'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="sidebar-nav"]',
      content: t('tour.steps.navigation', 'Navigate between questions'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="question-title"]',
      content: t('tour.steps.questionTitle', 'Question title and points'),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="flag"]',
      content: t('tour.steps.flag', 'Flag a question to review later (shortcut: M)'),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="answers"]',
      content: t('tour.steps.answers', 'Choose your answer here'),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="actions"]',
      content: t('tour.steps.actions', 'Submit, next, previous; Shift+Enter to submit'),
      placement: 'top',
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
      onComplete();
    }
  };

  if (!isRunning) return null;

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          primaryColor: '#2563eb',
          textColor: '#334155',
          width: 320,
          zIndex: 1000,
        },
        tooltip: {
          borderRadius: 8,
          fontSize: 14,
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 600,
        },
        buttonNext: {
          backgroundColor: '#2563eb',
          fontSize: 13,
          padding: '8px 16px',
          borderRadius: 6,
        },
        buttonBack: {
          color: '#64748b',
          fontSize: 13,
        },
        buttonSkip: {
          color: '#64748b',
          fontSize: 13,
        },
      }}
      locale={{
        back: t('tour.buttons.back', i18n.language === 'vi' ? 'Quay lại' : 'Back'),
        close: t('tour.buttons.close', i18n.language === 'vi' ? 'Đóng' : 'Close'),
        last: t('tour.buttons.finish', i18n.language === 'vi' ? 'Hoàn tất' : 'Finish'),
        next: t('tour.buttons.next', i18n.language === 'vi' ? 'Tiếp' : 'Next'),
        skip: t('tour.buttons.skip', i18n.language === 'vi' ? 'Bỏ qua' : 'Skip'),
      }}
    />
  );
}
